import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Bell,
  ChevronDown,
  Flag,
  ScrollText,
  DollarSign,
  ChevronRight,
  Home,
  PanelLeftClose,
  PanelLeft,
  Crown,
  Shield,
  Radar,
  Gavel,
  Receipt,
} from 'lucide-react';

export function AdminLayout({ children }) {
  const { user, logout, unreadCount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Persist sidebar state in localStorage to prevent reset on navigation
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('adminSidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(() => location.pathname.startsWith('/admin/analytics'));
  const [moderationExpanded, setModerationExpanded] = useState(() => location.pathname.startsWith('/admin/moderation'));

  // Save sidebar state to localStorage when it changes
  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('adminSidebarOpen', JSON.stringify(newState));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const mainNavItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/audit-logs', label: 'Audit Logs', icon: ScrollText },
  ];

  const moderationItems = [
    { path: '/admin/moderation/reports', label: 'Community Reports', icon: Flag },
    { path: '/admin/moderation/detections', label: 'Auto Detections', icon: Radar },
    { path: '/admin/moderation/discipline', label: 'User Discipline', icon: Gavel },
  ];

  const analyticsItems = [
    { path: '/admin/analytics', label: 'Overview', icon: BarChart3 },
    { path: '/admin/analytics/subscriptions', label: 'Subscriptions', icon: Crown },
    { path: '/admin/analytics/revenue', label: 'Revenue', icon: DollarSign },
    { path: '/admin/billing', label: 'Billing', icon: Receipt },
  ];

  const bottomNavItems = [
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isModerationActive = moderationItems.some(item => isActive(item.path));
  const isAnalyticsActive = analyticsItems.some(item => isActive(item.path));

  // Navigate without changing sidebar state
  const handleNavClick = (path) => {
    navigate(path);
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path, item.exact);
    
    return (
      <button
        key={item.path}
        onClick={() => handleNavClick(item.path)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
          active 
            ? 'bg-[#C9956C] text-white' 
            : 'text-white/70 hover:bg-white/10 hover:text-white'
        } ${!sidebarOpen ? 'justify-center' : ''}`}
        title={!sidebarOpen ? item.label : undefined}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {sidebarOpen && <span className="font-medium">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - w-20 when collapsed for better logo display */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#1E3A5F] text-white transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/10 flex items-center justify-center">
          <button onClick={() => handleNavClick('/admin')} className="flex items-center justify-center">
            {sidebarOpen ? (
              <img 
                src="/logo-wantd-white.png" 
                alt="WANTD" 
                className="h-8"
              />
            ) : (
              <img 
                src="/logo-n-white.png" 
                alt="WANTD" 
                className="h-10 w-10 object-contain"
              />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Main nav items */}
          {mainNavItems.map((item) => renderNavItem(item))}

          {/* Moderation section */}
          <div>
            {sidebarOpen ? (
              <>
                <button
                  onClick={() => setModerationExpanded(!moderationExpanded)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isModerationActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Moderation</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 transition-transform ${moderationExpanded ? 'rotate-90' : ''}`} />
                </button>

                {moderationExpanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-white/20 pl-4">
                    {moderationItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavClick(item.path)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                            active
                              ? 'bg-[#C9956C] text-white'
                              : 'text-white/60 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-1">
                {moderationItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className={`w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors ${
                        active
                          ? 'bg-[#C9956C] text-white'
                          : 'text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                      title={item.label}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Analytics section */}
          <div>
            {sidebarOpen ? (
              // Expanded mode - show dropdown
              <>
                <button
                  onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isAnalyticsActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Analytics</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 transition-transform ${analyticsExpanded ? 'rotate-90' : ''}`} />
                </button>
                
                {analyticsExpanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-white/20 pl-4">
                    {analyticsItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavClick(item.path)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                            active 
                              ? 'bg-[#C9956C] text-white' 
                              : 'text-white/60 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              // Collapsed mode - show icons only
              <div className="space-y-1">
                {analyticsItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className={`w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors ${
                        active 
                          ? 'bg-[#C9956C] text-white' 
                          : 'text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                      title={item.label}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Divider */}
          <div className="my-4 border-t border-white/10"></div>
          
          {/* Bottom nav items */}
          {bottomNavItems.map((item) => renderNavItem(item))}
          
          {/* Back to Main Site */}
          <button
            onClick={() => handleNavClick('/')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
            title={!sidebarOpen ? 'Back to Site' : undefined}
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium text-sm">Back to Site</span>}
          </button>
        </nav>

        {/* Bottom section */}
        <div className="p-3 border-t border-white/10 space-y-2">
          {/* Collapse/Expand button */}
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white w-full transition-colors"
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </button>
          
          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 w-full transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
            title={!sidebarOpen ? 'Sign Out' : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">
              {mainNavItems.find(item => isActive(item.path, item.exact))?.label ||
               moderationItems.find(item => isActive(item.path))?.label ||
               analyticsItems.find(item => isActive(item.path))?.label ||
               'Admin'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative p-2 hover:bg-gray-100 rounded-lg">
              <Bell className="w-5 h-5 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg"
              >
                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-sm">
                  {user?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-xs text-purple-600 font-bold uppercase">{user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                    <p className="text-xs text-purple-600 font-bold uppercase">{user?.role}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
